import React, { FunctionComponent, useMemo, useContext } from 'react';
import EDView from '../../../../../../BaseComponents/View/EDView';
import {
  InternalError,
  internalErrorBusinessComp,
} from '../../../../../../Components/InternalError/InternalError';
import { standardkomponent } from '../businessCollectionComponents';
import {
  IMasterConfig,
  IDetailConfig,
} from '../../../../../../Types/MasterDetailTypes';
import {
  IClientBusinessModel,
  IClientAssociationModel,
} from '../../../../../../Types/ClientModelTypes';
import { IColumnDef } from '../../../../../../Types/ColumnDefType';
import { findCollectionStatic } from '../../../../../../Model/avenyStaticModelUtils';
import MasterComp from './AssociationListMaster';
import { MdModalRoute } from '../../../../../../Components/MasterDetail/Components/MdModal/MdModalRoute';
import { TVisningstyp } from '../BusinessDataSet/BusinessDataSet';
import MdInline from '../../../../../../Components/MasterDetail/Components/MdInline/MdInline';
import { IMenuProps } from '../../FunctionMenu/FunctionMenu/FunctionMenu';
import { BusinessDataFormContext } from '../BusinessDataForm/BusinessDataForm';
import { VisningsTypContext } from '../VisningsTypContext/VisningsTypContext';
import { useItemFunctionMenuItemsType } from '../../../../../../Utils/ObjektVyUtils';
import { Theme } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { BusinessCollectionMenuItemsType } from '../BusinessCollectionMenu/Utils/BusinessCollectionMenuUtils';
import {
  findBusinessconfig,
  ICustomBusinessConfig,
} from '../../../Const/BusinessConfigs';
import { BusinessConfigContextProvider } from '../../../../../../Components/BusinessConfigContext/BusinessConfigContext';
import { ActiveRoutineContext } from '../../../../../Routing/ActiveRoutine/ActiveRoutineContext';
import { GridCellParams } from '@mui/x-data-grid-pro';
import { rutiner } from '../../../Const/Rutiner';

const createMasterConfig = (
  name: string,
  association: IClientAssociationModel,
  collection: IClientBusinessModel,
  associatedCollection: IClientBusinessModel,
  ownId: string | null,
  config?: IAssociationListConfig,
  associationPath?: string,
  rutinKod?: string
): IMasterConfig => ({
  comp: MasterComp(
    association,
    collection,
    associatedCollection,
    ownId,
    config || {},
    associationPath,
    rutinKod
  ),
  caption: collection.RootType.SBLabel,
});

const detailComp = (collectionName: string, inline?: boolean) =>
  standardkomponent(collectionName, inline);

const createDetailConfig = (
  collection?: IClientBusinessModel,
  fullPage?: boolean,
  inline?: boolean
): IDetailConfig => ({
  comp: (collection
    ? detailComp(collection.CollectionName, inline)
    : internalErrorBusinessComp(
        '547efbf9-39a1-4abc-afe9-951ebfa17428',
        'collection saknas'
      )) as IDetailConfig['comp'],
  caption: collection ? collection.RootType.SOLabel : 'collection saknas',
  fullPage: fullPage,
});

export interface ISumColumn {
  columnName: string;
}

export interface IAssociationListConfig {
  customDefaultColumns?: IColumnDef[];
  inCard?: boolean;
  detailsFullPage?: boolean;
  defaultVisningstyp?: TVisningstyp;
  noCaption?: boolean;
  useFunctionMenuItems?: useItemFunctionMenuItemsType;
  hideDefaultFunktionsMenyItems?: boolean;
  customSOLabel?: string;
  disabled?: boolean;
  businessCollectionMenuItems?: BusinessCollectionMenuItemsType;
  /** Id of an item that will become its own button to the left of the function button */
  highlightedBusinessCollectionMenuItem?: string;
  integrated?: boolean;
  /** En businessconfig som överstyr default för den associerade typen */
  customBusinessConfig?: ICustomBusinessConfig;
  /** Objekt med kolumnnamn som ska summeras och ritas ut som en rad i tabellen */
  sumColumns?: ISumColumn[];
  showDeleteIcon?: boolean;
  objectLinksEnabled?: boolean;
  isCellDisabled?: (gridCellParams: GridCellParams) => boolean;
  disableAddRefetchButtonQuery?: boolean;
  /** Sätt höjden för att alla rader ska få plats */
  autoHeight?: boolean;
}

const useStyles = makeStyles()(
  (theme: Theme) =>
    ({
      detailsRightGridded: {
        '& [class*="MuiButtonBase"] p': {
          display: 'inline-block',
        },
      },
    } as const)
);

export interface IAssociationListProps {
  /** id för objektet som associationen utgår från */
  ownId: string | null;
  /** Vilka associerade objekt som ska visas, kan vara namnet på en 0-många association eller en path med associationer åtskiljda med punkt */
  associationPath?: string;
  /** collectionName för objektet som associationen utgår från */
  collectionName: string;
  associationName: string;
  /** Objekt för inställningar som inte är obligatoriska.
   *  customDefaultColumns, defaulta till andra kolumner än de som är default för typen.
   *  ska kompletteras med fler inställning för att välja andra list och detaljvyer
   */
  config?: IAssociationListConfig;
  associatedCollectionFieldName?: string;
  vyMenu?: (props: IMenuProps) => React.ReactElement<any>;
  leftPadding?: boolean;
}

/**
Listar de associerade objekten för en "till många"-association.
*/
export const AssociationList: FunctionComponent<
  React.PropsWithChildren<IAssociationListProps>
> = props => {
  const { classes, cx } = useStyles();
  const { visningsTyp, showDetailsRight } = useContext(VisningsTypContext);
  const activeRoutineContext = useContext(ActiveRoutineContext);
  const formContext = useContext(BusinessDataFormContext);
  const config = useMemo<IAssociationListConfig>(
    () => ({
      ...props.config,
      disabled: formContext?.formState.editMode && !props.config?.integrated,
    }),
    [props.config, formContext?.formState.editMode]
  );

  const collection = findCollectionStatic(props.collectionName);
  const association: IClientAssociationModel | undefined =
    collection && collection.Associations[props.associationName];

  const associatedCollection =
    association && findCollectionStatic(association.AssociatedCollectionName);
  const masterConfig = useMemo(
    () =>
      collection &&
      associatedCollection &&
      createMasterConfig(
        props.collectionName,
        association,
        collection,
        associatedCollection,
        props.ownId,
        config,
        props.associationPath,
        activeRoutineContext?.rutinInfo.rutinKod
      ),
    [
      collection,
      associatedCollection,
      props.collectionName,
      props.ownId,
      props.associationPath,
      association,
      config,
      activeRoutineContext?.rutinInfo.rutinKod,
    ]
  );
  const detailConfig = useMemo(
    () =>
      createDetailConfig(
        associatedCollection,
        config?.detailsFullPage,
        visningsTyp === 'expanderbar lista'
      ),
    [associatedCollection, visningsTyp, config]
  );

  const customConfig = useMemo(
    () =>
      associatedCollection &&
      props.config?.customBusinessConfig && {
        [associatedCollection?.CollectionName]:
          props.config?.customBusinessConfig,
      },
    [associatedCollection, props.config?.customBusinessConfig]
  );

  const rutinConfig = activeRoutineContext?.rutinInfo?.rutinKod
    ? rutiner[activeRoutineContext.rutinInfo.rutinKod]
    : undefined;
  const detailRoutineAssociationConfig =
    rutinConfig && rutinConfig.config && associatedCollection
      ? rutinConfig.config[associatedCollection.CollectionName]
      : undefined;
  const detailBusinessConfig =
    associatedCollection &&
    findBusinessconfig(associatedCollection.CollectionName);

  const route =
    visningsTyp !== 'expanderbar lista' &&
    (props.config?.objectLinksEnabled ??
      detailRoutineAssociationConfig?.objectLinksEnabled ??
      detailBusinessConfig?.objectLinksEnabled);

  if (collection && masterConfig) {
    if (association) {
      if (associatedCollection) {
        return (
          <BusinessConfigContextProvider value={customConfig}>
            <EDView
              style={
                props.config?.inCard
                  ? props.leftPadding
                    ? { width: '100%', display: 'flex', paddingLeft: 8 }
                    : { width: '100%', display: 'flex' }
                  : {
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                    }
              }
              className={cx({
                [classes.detailsRightGridded]: showDetailsRight,
              })}
            >
              {route ? (
                <MdModalRoute
                  detailRouteName={association.AssociationName}
                  master={masterConfig}
                  detail={detailConfig}
                  collection={associatedCollection}
                />
              ) : (
                <MdInline master={masterConfig} detail={detailConfig} />
              )}
            </EDView>
          </BusinessConfigContextProvider>
        );
      }
    }
  }
  return (
    <InternalError
      message={`CollectionName ${props.collectionName}, eller association ${props.associationName} saknas`}
      id={'b4362ff1-6ddd-46f1-9d94-7866000a71c3'}
    />
  );
};
